import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ContractRenameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentTitle: string;
  onRename: (newTitle: string) => Promise<void>;
}

const ContractRenameDialog = ({
  open,
  onOpenChange,
  currentTitle,
  onRename,
}: ContractRenameDialogProps) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(currentTitle);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(currentTitle);
    }
  }, [open, currentTitle]);

  const trimmed = title.trim();
  const canSave = trimmed.length > 0 && trimmed !== currentTitle.trim() && !isSaving;

  const handleOpenChange = (next: boolean) => {
    if (isSaving) return;
    onOpenChange(next);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSave) return;

    setIsSaving(true);
    try {
      await onRename(trimmed);
      onOpenChange(false);
    } catch {
      // Error is handled by caller (toast/handleError).
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('ORGCONTRACTS_RENAME_DIALOG_TITLE')}</DialogTitle>
          <DialogDescription>
            {t('ORGCONTRACTS_RENAME_DIALOG_DESCRIPTION')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="contract-rename-input">
              {t('ORGCONTRACTS_RENAME_DIALOG_LABEL')}
            </Label>
            <Input
              id="contract-rename-input"
              value={title}
              autoFocus
              maxLength={200}
              disabled={isSaving}
              placeholder={t('ORGCONTRACTS_RENAME_DIALOG_PLACEHOLDER')}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSaving}
            >
              {t('ORGCONTRACTS_RENAME_DIALOG_CANCEL')}
            </Button>
            <Button type="submit" variant="primary" disabled={!canSave}>
              {isSaving && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('ORGCONTRACTS_RENAME_DIALOG_SAVE')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContractRenameDialog;
